import LinkButton from "~/components/LinkButton"

import { h1Style, imagePreview, h1Wrap, headerTextStyle } from "./article.css"
import { btnWrap } from "./index.css"

import { MetaFunction } from "@remix-run/node"
import TextBlock from "~/components/TextBlock"
import Footer from "~/components/Footer"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    { title: "Embracing Nature in Contemporary Design | Modern & Natural." },
    {
      name: "description",
      content:
        "Design breathtaking gardens with Rescape AI's garden design app. Blend nature and modern elements for stunning contemporary outdoor spaces.",
    },
    {
      property: "og:title",
      content: "Embracing nature in contemporary design",
    },
    {
      property: "og:description",
      content:
        "In the realm of landscape architecture, modern contemporary garden design has emerged as a captivating and innovative approach that blends sleek aesthetics with a deep appreciation for nature.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/styles/contemporary.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/article/nature-in-contemporary-design",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Embracing Nature in Contemporary Design | Modern & Natural.",
        description:
          "Design breathtaking gardens with Rescape AI's garden design app. Blend nature and modern elements for stunning contemporary outdoor spaces.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-01-18",
        dateModified: "2024-01-18",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/landing/modern-garden.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/article/nature-in-contemporary-design",
        },
        articleSection: [
          "Technology",
          "Landscape Design",
          "Contemporary Design",
          "Nature",
          "Artificial Intelligence",
          "Gardening",
        ],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/landing/modern-garden.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Embracing Nature in Modern Contemporary Garden&nbsp;Design
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <TextBlock
          title=""
          paragraphs={[
            `In the realm of landscape architecture, modern contemporary garden design has
            emerged as a captivating and innovative approach that blends sleek aesthetics with
            a deep appreciation for nature. Characterized by clean lines, minimalist elements,
            and a seamless integration of indoor and outdoor spaces, contemporary gardens
            offer a refreshing take on the traditional while fostering a harmonious connection
            with the environment. This article explores the key principles and elements that
            define modern contemporary garden design.`,
          ]}
          marginTop="4rem"
        />
        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton
            url="/article/contemporary-garden-ideas-with-rescape-ai"
            shouldPrefetch
          >
            Contemporary Garden Ideas
          </LinkButton>
        </div>
        <TextBlock
          title="Clean Lines and Structural Simplicity"
          paragraphs={[
            `Modern contemporary garden design is distinguished by its emphasis on clean,
          straight lines and structural simplicity. Geometric shapes, such as rectangles and
          squares, are often used to create a sense of order and organization within the
          outdoor space. This simplicity not only adds a touch of sophistication but also
          allows the natural elements to take center stage.`,
          ]}
          marginTop="4rem"
        />
        <img
          src="/marketing/garden-2.webp"
          alt="Clean AI contemporary design with pagoda with table"
          className={imagePreview}
          style={{ marginTop: "4rem" }}
          loading="lazy"
        />
        <TextBlock
          title="Materials and Texture"
          paragraphs={[
            `The choice of materials plays a crucial role in contemporary garden design. Common
        materials include concrete, steel, glass, and wood. These materials contribute to
        a sleek and polished appearance while providing durability and functionality.
        Contrasting textures, such as smooth concrete against rough stone or soft grass,
        add depth and visual interest to the garden.`,
          ]}
          marginTop="4rem"
        />
        <TextBlock
          title="Integration of Indoor and Outdoor Spaces"
          paragraphs={[
            `Blurring the boundaries between indoor and outdoor spaces is a hallmark of
            contemporary garden design. Large glass doors, windows, or sliding walls
            seamlessly connect the interior and exterior, creating a fluid transition between
            the two. This integration allows for a more immersive experience, inviting the
            natural surroundings into the living spaces and vice versa.`,
          ]}
          marginTop="4rem"
        />

        <img
          src="/marketing/garden-4.webp"
          alt="A contemporary design planted garden, AI generated with Rescape AI"
          className={imagePreview}
          style={{ marginTop: "4rem" }}
          loading="lazy"
        />
        <TextBlock
          title="Sustainable Practices"
          paragraphs={[
            `Sustainability is a growing concern in modern garden design. Eco-friendly
            features, such as rain gardens, green roofs, and native plantings, are integrated
            to promote biodiversity and reduce the environmental impact. Water-efficient
            irrigation systems and the use of recycled materials further contribute to a
            garden that aligns with ecological principles.`,
          ]}
          marginTop="4rem"
        />
        <TextBlock
          title="Focal Points and Artistic Elements"
          paragraphs={[
            `Contemporary gardens often incorporate focal points and artistic elements to
            enhance visual appeal. Sculptures, water features, or carefully placed plantings
            can serve as focal points, drawing attention and creating a sense of balance
            within the overall design. These elements add a touch of individuality and
            artistic expression to the garden.`,
          ]}
          marginTop="4rem"
        />
        <img
          src="/marketing/garden-3.webp"
          alt="Tasteful modern plantings for a contemporary garden design, unique and tailored. Generated with Rescape AI"
          className={imagePreview}
          style={{ marginTop: "4rem" }}
          loading="lazy"
        />
        <TextBlock
          title="Functional Outdoor Spaces"
          paragraphs={[
            `Beyond aesthetics, modern contemporary gardens are designed with functionality in
            mind. Thoughtfully planned outdoor living spaces, such as seating areas, dining
            spaces, and fire pits, provide opportunities for relaxation and socialization. The
            garden becomes an extension of the home, encouraging inhabitants to enjoy the
            outdoors in comfort and style.`,
          ]}
          marginTop="4rem"
        />
        <TextBlock
          title="Plant Selection and Maintenance"
          paragraphs={[
            `Plant choices in contemporary gardens often lean towards a restrained palette with
            an emphasis on form and structure. Ornamental grasses, sculptural shrubs, and
            carefully pruned trees contribute to the overall composition. Low-maintenance
            plantings are favored, aligning with the modern lifestyle that values simplicity
            and efficiency.`,
          ]}
          marginTop="4rem"
        />
        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Sign up now
          </LinkButton>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default Index
